
import React, { Suspense, lazy, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generatePublicUrl } from '../../urlConfig';
import { TodoTaskViewModal } from '../Todo/TodoChatModals';
import ProductCardModal from './ProductCardModal';
import '../../scss/components/_chat-product-card.scss';
import { dueText } from '../Todo/todoUtils';

// Карточка заказа тянет за собой весь редактор заказа — грузим её, только когда открыли.
const ChatOrderCardModal = lazy(() => import('./ChatOrderCardModal'));

const META = {
    product: { label: 'Товар', icon: '▣' },
    order: { label: 'Заказ', icon: '▤' },
    task: { label: 'Задание офиса', icon: '✓' },
    todo_task: { label: 'Задача', icon: '✓' },
    member: { label: 'Сотрудник', icon: '●' },
    sales_session: { label: '3D-сессия', icon: '◈' },
    warehouse_material: { label: 'Склад', icon: '▦' },
    warehouse: { label: 'Склад', icon: '▦' },
    material: { label: 'Материал', icon: '◆' },
};
const TASK_STATUS = { published: 'Доступно', accepted: 'Принято', in_progress: 'В работе', review: 'На проверке', done: 'Выполнено', rework: 'На доработке', cancelled: 'Отменено' };
const SESSION_STATUS = { active: 'Активна', closed: 'Закрыта', converted_to_order: 'Создан заказ', archived: 'Архив' };
const UNIT = { pcs: 'шт.', m: 'м', m2: 'м²', m3: 'м³', kg: 'кг', t: 'т', l: 'л', set: 'компл.' };
const formatQty = (value, unit) => `${Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 3 })} ${UNIT[unit] || unit || ''}`.trim();
const dateText = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU');
};
const imageSrc = (value) => {
    const image = String(value || '');
    if (!image) return '';
    if (/^(https?:)?\/\//i.test(image) || /^(blob:|data:image\/)/i.test(image) || image.startsWith('/media/')) return image;
    return generatePublicUrl(image);
};

/** «2 размера», «5 размеров», «21 материал». */
const countText = (value, one, few, many) => {
    const n = Math.abs(Number(value) || 0);
    const mod10 = n % 10;
    const mod100 = n % 100;
    const word = mod10 === 1 && mod100 !== 11 ? one : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many;
    return `${n} ${word}`;
};

const rub = (value) => `${Math.round(Number(value || 0)).toLocaleString('ru-RU')} ₽`;

/**
 * Цены товара в карточке: розница на сайте и для подписки. Цену
 * изготовления в чат не показываем — в старых сообщениях она стояла в
 * подзаголовке («… · 12 000 ₽»), её убираем.
 */
const productPrices = (entity) => {
    const p = entity.payload || {};
    const prefix = p.priceFrom ? 'от ' : '';
    return [
        Number(p.publicPrice) > 0 ? { label: 'На сайте', value: prefix + rub(p.publicPrice), main: true } : null,
        Number(p.subscriberPrice) > 0 ? { label: 'По подписке', value: prefix + rub(p.subscriberPrice) } : null,
    ].filter(Boolean);
};

const cleanSubtitle = (entity) => {
    const subtitle = String(entity.subtitle || '');
    if (entity.type !== 'product') return subtitle;
    return subtitle.split(' · ').filter((part) => !/₽\s*$/.test(part)).join(' · ');
};

const factsFor = (entity) => {
    const p = entity.payload || {};
    if (entity.type === 'product') return [
        p.article != null ? `Арт. ${p.article}` : '',
        p.categoryName,
        p.materialName,
        Number(p.materialsCount) > 1 ? countText(p.materialsCount, 'материал', 'материала', 'материалов') : '',
        Number(p.sizesCount) > 1 ? countText(p.sizesCount, 'размер', 'размера', 'размеров') : '',
        p.hasModel ? 'Есть 3D' : '',
    ];
    if (entity.type === 'order') return [p.orderNumber ? `№ ${p.orderNumber}` : '', p.status, p.paymentStatus, dateText(p.createdAt)];
    if (entity.type === 'task') return [TASK_STATUS[p.status] || p.status, p.checklistTotal != null ? `Чек-лист ${p.checklistDone || 0}/${p.checklistTotal}` : '', p.dueAt ? `до ${dateText(p.dueAt)}` : ''];
    if (entity.type === 'todo_task') return [p.priority === 'high' ? 'Важная' : '', p.dueAt ? `Срок: ${dueText(p.dueAt)}` : '', 'Открыть задачу'];
    if (entity.type === 'sales_session') return [p.sessionId, SESSION_STATUS[p.status] || p.status, p.currentTab];
    if (entity.type === 'warehouse_material') return [p.sku ? `SKU ${p.sku}` : '', p.warehouseName, p.isStockTracked === false ? 'Без учёта остатков' : `Доступно ${formatQty(p.available, p.unit || 'pcs')}`, p.stockState === 'out' ? 'Нет в наличии' : ''];
    if (entity.type === 'member') return [p.role, p.email];
    return [p.status, p.subtitle];
};

export default function MessageEntityCard({ message, entity: entityProp, officeId = null }) {
    const navigate = useNavigate();
    const [taskOpen, setTaskOpen] = useState(false);
    const [productOpen, setProductOpen] = useState(false);
    const [orderOpen, setOrderOpen] = useState(false);
    const entity = entityProp || message?.entity;
    if (!entity || !entity.type || entity.type === 'none') return null;

    const meta = META[entity.type] || { label: 'Объект', icon: '◆' };
    const payload = entity.payload || {};
    const image = imageSrc(entity.image || payload.image || payload.previewImage || payload.profilePicture);
    const facts = factsFor(entity).filter(Boolean).slice(0, 6);
    const prices = entity.type === 'product' ? productPrices(entity) : [];
    const subtitle = cleanSubtitle(entity);
    const title = String(entity.title || payload.title || meta.label);
    // ADMIN and office FRONT have separate routers. Never open a FRONT-only link
    // such as /adminLayout on the wrong host, or grant access based on a card.
    // Заказ открывается карточкой прямо в чате, без перехода в раздел.
    const canOpenProducts = entity.type === 'product';
    const canOpenUsers = entity.type === 'member';
    const navigateTo = canOpenProducts ? '/category' : canOpenUsers ? '/users' : null;
    const id = String(entity.id?._id || entity.id || '');
    const taskId = entity.type === 'todo_task' ? id : '';
    // Товар — подробная карточка прямо в чате (как в приложении).
    const productId = entity.type === 'product' ? id : '';
    // Заказ — та же карточка, что в разделе «Заказы», поверх чата.
    const orderId = entity.type === 'order' ? id : '';

    // Товар — своя компактная карточка: фото, название, цены по камню.
    if (entity.type === 'product') {
        const stones = (Array.isArray(payload.stonePrices) ? payload.stonePrices : [])
            .filter((stone) => Number(stone?.subscriberPrice) > 0 || Number(stone?.publicPrice) > 0 || Number(stone?.millingPrice) > 0);
        const withMilling = stones.some((stone) => Number(stone?.millingPrice) > 0);
        const metaLine = [payload.article != null ? `Арт. ${payload.article}` : '', payload.categoryName].filter(Boolean).join(' · ') || subtitle;
        const chips = [
            payload.materialName && !stones.length ? payload.materialName : '',
            Number(payload.sizesCount) > 1 ? countText(payload.sizesCount, 'размер', 'размера', 'размеров') : '',
            payload.hasModel ? '3D' : '',
        ].filter(Boolean);

        return (
            <>
            <div className="chat-product-msg-AO" data-chat-no-select="true" data-entity-type="product">
                <button type="button" className="chat-product-msg-AO__photo" onClick={() => setProductOpen(true)} title="Открыть карточку товара">
                    {image ? <img src={image} alt={title} loading="lazy" decoding="async" /> : <span>▣</span>}
                </button>
                <div className="chat-product-msg-AO__main">
                    <small className="chat-product-msg-AO__type">Товар</small>
                    <strong className="chat-product-msg-AO__title">{title}</strong>
                    {metaLine ? <span className="chat-product-msg-AO__meta">{metaLine}</span> : null}
                    {chips.length ? (
                        <div className="chat-product-msg-AO__chips">{chips.map((chip) => <span key={chip}>{chip}</span>)}</div>
                    ) : null}

                    {stones.length ? (
                        <div className="chat-product-msg-AO__stones">
                            <div className={`chat-product-msg-AO__stones-head ${withMilling ? 'has-milling' : ''}`}>
                                <span /><small>Подписка</small><small>Обычная</small>{withMilling ? <small>Фрезеровка</small> : null}
                            </div>
                            {stones.slice(0, 6).map((stone) => (
                                <div key={stone.key || stone.name} className={`chat-product-msg-AO__stone ${withMilling ? 'has-milling' : ''}`}>
                                    <span>{stone.name}</span>
                                    <b className="is-subscription">{Number(stone.subscriberPrice) > 0 ? rub(stone.subscriberPrice) : '—'}</b>
                                    <b>{Number(stone.publicPrice) > 0 ? rub(stone.publicPrice) : '—'}</b>
                                    {withMilling ? <b className="is-milling">{Number(stone.millingPrice) > 0 ? rub(stone.millingPrice) : '—'}</b> : null}
                                </div>
                            ))}
                        </div>
                    ) : prices.length ? (
                        <div className="chat-product-msg-AO__prices">
                            {prices.map((price) => (
                                <span key={price.label} className={price.main ? 'is-main' : ''}>
                                    <small>{price.label}</small>
                                    <b>{price.value}</b>
                                </span>
                            ))}
                        </div>
                    ) : null}

                    <div className="chat-product-msg-AO__actions">
                        <button type="button" className="is-primary" onClick={() => setProductOpen(true)}>Карточка товара</button>
                        {!!id && (
                            <button
                                type="button"
                                onClick={async () => { try { await navigator.clipboard.writeText(id); } catch (_) { /* clipboard permissions */ } }}
                                title="Копировать ID товара"
                            >
                                ID ⧉
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {productOpen ? <ProductCardModal productId={id} title={title} onClose={() => setProductOpen(false)} /> : null}
            </>
        );
    }

    return (
        <>
        <div className={`chat-entity-card-AO chat-entity-card-AO--extended is-${entity.type}`} data-chat-no-select="true" data-entity-type={entity.type}>
            <div className="chat-entity-card-AO__image">
                {image ? <img src={image} alt={title} loading="lazy" decoding="async" /> : <span className="chat-entity-card-AO__fallback">{meta.icon}</span>}
            </div>
            <div className="chat-entity-card-AO__main">
                <small>{meta.label}</small>
                <strong>{title}</strong>
                {subtitle && <p>{subtitle}</p>}
                {!!prices.length && (
                    <div className="chat-entity-card-AO__prices" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, margin: '6px 0 2px' }}>
                        {prices.map((price) => (
                            <span key={price.label} style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                <small style={{ opacity: 0.7 }}>{price.label}</small>
                                <b style={{ fontSize: 15, color: price.main ? '#2e9d57' : 'inherit' }}>{price.value}</b>
                            </span>
                        ))}
                    </div>
                )}
                {!!facts.length && <div className="chat-entity-card-AO__facts">{facts.map((fact, i) => <span key={`${i}-${fact}`}>{fact}</span>)}</div>}
                <div className="chat-entity-card-AO__actions">
                    {navigateTo && <button type="button" onClick={() => navigate(navigateTo)} title="Открыть соответствующий раздел ADMIN">Открыть раздел ↗</button>}
                    {!!productId && <button type="button" onClick={() => setProductOpen(true)} title="Фото, размеры и цены товара, не покидая чат">Карточка товара</button>}
                    {!!orderId && <button type="button" onClick={() => setOrderOpen(true)} title="Позиции, оплаты и статусы заказа, не покидая чат">Карточка заказа</button>}
                    {!!taskId && <button type="button" onClick={() => setTaskOpen(true)} title="Просмотреть задачу, не покидая чат">Открыть задачу ↗</button>}
                    {!!id && <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(id); } catch (_) { /* clipboard permissions */ } }} title="Копировать идентификатор объекта">ID <span aria-hidden="true">⧉</span></button>}
                </div>
            </div>
        </div>
        {!!productId && productOpen && (
            <ProductCardModal productId={productId} title={title} onClose={() => setProductOpen(false)} />
        )}
        {!!orderId && orderOpen && (
            <Suspense fallback={null}>
                <ChatOrderCardModal orderId={orderId} onClose={() => setOrderOpen(false)} />
            </Suspense>
        )}
        {!!taskId && taskOpen && (
            <TodoTaskViewModal taskId={taskId} onClose={() => setTaskOpen(false)} />
        )}
        </>
    );
}
